import {UIGameBaseTileController} from './UIGameTileControllerBase'
import {UIGameGeneralShoreTileController} from './UIGameGeneralShoreTileController'
import {UIGameManager} from '../UIGameManager'
import {UIGameMechanic} from '../UIGameEventController'
import {GameStateEnums} from '../../../../../shared/GameEnums'
import {UIGameMapTileView} from './UIGameMapTileView'
import {BeginnerModeActionState} from '@colonist/game-state-types'
import {HiddenUIElement, getWaitMSForAction} from '@colonist/game-beginner-mode'
import {UIGameFlickerAnimation} from '../../ui_canvas_engine/animation/UIGameFlickerAnimation'
import {TileHexType} from '@colonist/game-states'
import {Point} from '@colonist/game-mechanic-validators'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {UIGameMapDiceProbabilityView} from './UIGameMapDiceProbabilityView'
import {UIGameGoToPointAnimation} from '../../ui_canvas_engine/animation/UIGameGoToPointAnimation'

export class UIGameGeneralTileController extends UIGameBaseTileController implements UIGameMechanic {
    private tilesToHighlight: UIGameMapTileView[]

    private probabilityViewAnimationWaitMS: number = 1300
    private isBeginnerModeEnabled: boolean = false

    constructor(uiGameManager: UIGameManager) {
        super(uiGameManager)
        this.uiGameManager.eventController.registerUIGameMechanic(this)

        this.tilesToHighlight = []

        this.drawTiles()

        new UIGameGeneralShoreTileController(this.uiGameManager, this.uiGameManager.gameController.mapValidator.fogTileGroupStates)
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.DiceStateUpdated: this.rolledTile(data); break
            case UIGameEventEnums.MapStateUpdated: this.updateDiceProbabilities(); break
            case UIGameEventEnums.BeginnerModeStart: this.onBeginnerModeStart(); break
            case UIGameEventEnums.BeginnerModeActionUpdated: this.onBeginnerModeActionUpdate(data); break
            case UIGameEventEnums.DiceNumberAnimationEnd: this.onDiceNumberAnimationEnd(data); break
            default: break
        }
    }

    private drawTiles() {
        for(const tile of this.uiGameManager.gameState.mapState.tileState.tiles) {
            const tileView = this.drawTile(tile)
            this.uiGameManager.mapController.tileViews.push(tileView)
        }
    }

    private rolledTile(animate: boolean) {
        if(!this.uiGameManager.gameController.diceState.diceThrown) return
        if(!animate) return

        const diceNumber = this.uiGameManager.gameController.diceState.dice1 + this.uiGameManager.gameController.diceState.dice2
        for(const tile of this.uiGameManager.gameState.mapState.tileState.tiles) {
            if(tile.diceNumber != diceNumber) continue

            const tileView = this.uiGameManager.mapController.getTileViewForHexFace(tile.hexFace)
            if(tileView == undefined) continue

            this.tilesToHighlight.push(tileView)
        }

        if(this.isBeginnerModeEnabled) return
        this.animateTiles()
    }

    private animateTiles() {
        const animationDuration = 2
        for(const tileView of this.tilesToHighlight) {
            tileView.highlightTileForTime(animationDuration)
        }
        this.tilesToHighlight = []
    }

    private updateDiceProbabilities() {
        const tileViewsUpdatingDiceNumbers = this.getTileViewsUpdatingDiceProbabilities()
        if(tileViewsUpdatingDiceNumbers.length == 0) return

        if(this.areDiceNumbersSwapping(tileViewsUpdatingDiceNumbers)) {
            this.animateDiceNumbersSwapping(tileViewsUpdatingDiceNumbers)
            return
        }
        this.redrawDiceProbability(tileViewsUpdatingDiceNumbers)
    }

    private areDiceNumbersSwapping(tileViewsUpdatingDiceNumbers: UIGameMapTileView[]): boolean {
        if(tileViewsUpdatingDiceNumbers.length != 2) return false

        const tile1ProbabilityView = tileViewsUpdatingDiceNumbers[0].probabilityView
        const tile2ProbabilityView = tileViewsUpdatingDiceNumbers[1].probabilityView
        const isExistingAnimationInProgress = tile1ProbabilityView?.swapDiceProbabilityAnimation?.isAnimationInProgress === true || tile2ProbabilityView?.swapDiceProbabilityAnimation?.isAnimationInProgress === true
        if(isExistingAnimationInProgress) {
            tile1ProbabilityView?.swapDiceProbabilityAnimation?.stop()
            tile2ProbabilityView?.swapDiceProbabilityAnimation?.stop()
            return this.areDiceNumbersSwapping(tileViewsUpdatingDiceNumbers)
        }

        const tile1OriginalDiceNumber = tileViewsUpdatingDiceNumbers[0].diceNumber
        const tile2OriginalDiceNumber = tileViewsUpdatingDiceNumbers[1].diceNumber

        const tile1NewDiceNumber = this.uiGameManager.gameController.mapValidator.tileState.getTileHex(tileViewsUpdatingDiceNumbers[0].hexFace)?.diceNumber
        const tile2NewDiceNumber = this.uiGameManager.gameController.mapValidator.tileState.getTileHex(tileViewsUpdatingDiceNumbers[1].hexFace)?.diceNumber

        if(tile1OriginalDiceNumber != tile2NewDiceNumber) return false
        if(tile2OriginalDiceNumber != tile1NewDiceNumber) return false

        return true
    }

    private getTileViewsUpdatingDiceProbabilities(): UIGameMapTileView[] {
        const tileViewsBeingUpdated: UIGameMapTileView[] = []
        for(const tileHex of this.uiGameManager.gameState.mapState.tileState.tiles) {
            const tileView = this.uiGameManager.mapController.getTileViewForHexFace(tileHex.hexFace)
            if(tileView == undefined) continue

            const isTileDiceProbabilityViewAnimating = tileView.probabilityView?.swapDiceProbabilityAnimation !== undefined
            const isDiceNumberSame = tileView.diceNumber == tileHex.diceNumber
            if(isDiceNumberSame && !isTileDiceProbabilityViewAnimating) continue

            tileViewsBeingUpdated.push(tileView)
        }
        return tileViewsBeingUpdated
    }

    private animateDiceNumbersSwapping(tileViews: UIGameMapTileView[]) {
        if(tileViews.length != 2) return

        const firstProbabilityView = tileViews[0].probabilityView
        const secondProbabilityView = tileViews[1].probabilityView
        if(firstProbabilityView == undefined) return
        if(secondProbabilityView == undefined) return

        const firstProbabilityViewStartingPosition = tileViews[0].getDiceProbabilityMapPoint()
        if(firstProbabilityViewStartingPosition == undefined) return

        const secondProbabilityViewStartingPosition = tileViews[1].getDiceProbabilityMapPoint()
        if(secondProbabilityViewStartingPosition == undefined) return

        const firstDiceNumber = tileViews[0].diceNumber
        if(firstDiceNumber == undefined) return
        const secondDiceNumber = tileViews[1].diceNumber
        if(secondDiceNumber == undefined) return

        this.createSwapDiceProbabilityAnimation(firstProbabilityView, firstProbabilityViewStartingPosition, secondProbabilityViewStartingPosition, firstDiceNumber, tileViews[1])
        this.createSwapDiceProbabilityAnimation(secondProbabilityView, secondProbabilityViewStartingPosition, firstProbabilityViewStartingPosition, secondDiceNumber, tileViews[0])
    }

    private createSwapDiceProbabilityAnimation(diceProbabilityView: UIGameMapDiceProbabilityView, startingPosition: Point, endPosition: Point, originalDiceNumber: number, newTileView: UIGameMapTileView) {
        if(diceProbabilityView.parent != undefined) diceProbabilityView.parent.removeChild(diceProbabilityView)
        this.uiGameManager.mapController.mapView.addChild(diceProbabilityView)
        diceProbabilityView.x = startingPosition.x
        diceProbabilityView.y = startingPosition.y

        diceProbabilityView.swapDiceProbabilityAnimation = new UIGameGoToPointAnimation(diceProbabilityView, endPosition, 0.5)
        diceProbabilityView.swapDiceProbabilityAnimation.setOnStopCallback(() => {
            if(diceProbabilityView.parent != undefined) diceProbabilityView.parent.removeChild(diceProbabilityView)
            newTileView.drawProbability(originalDiceNumber)
        })
        diceProbabilityView.swapDiceProbabilityAnimation.start()
    }

    private redrawDiceProbability(tileViews: UIGameMapTileView[]) {
        for(const tileView of tileViews) {
            const tileHex = this.uiGameManager.gameState.mapState.tileState.getTileHex(tileView.hexFace)
            if(tileHex == undefined) continue

            tileView.drawProbability(tileHex.diceNumber)
        }
    }

    private onBeginnerModeStart() {
        this.isBeginnerModeEnabled = true
        for(const tileView of this.uiGameManager.mapController.tileViews) if(tileView.probabilityView != undefined) tileView.probabilityView.visible = false
        for(const tile of this.uiGameManager.gameState.mapState.tileState.tiles) {
            if(tile.tileType != TileHexType.Gold) continue
            const tileView = this.uiGameManager.mapController.getTileViewForHexFace(tile.hexFace)
            if(tileView == undefined) continue
            new UIGameFlickerAnimation(tileView).start()
        }
    }

    private onBeginnerModeActionUpdate(data: BeginnerModeActionState) {
        const baseWaitMS = 1300
        this.probabilityViewAnimationWaitMS = getWaitMSForAction(baseWaitMS, data.actionIndex)

        const areProbabilityViewsStillHidden: boolean = data.hiddenUIElements.includes(HiddenUIElement.TileProbabilityViews)
        if(areProbabilityViewsStillHidden) return
        this.revealProbabilityViews()
    }

    private revealProbabilityViews() {
        for(const tileView of this.uiGameManager.mapController.tileViews) {
            if(tileView.probabilityView != undefined) tileView.probabilityView.visible = true
        }
    }

    private onDiceNumberAnimationEnd(data?: number) {
        if(data == undefined) return this.uiGameManager.eventController.sendEvent(undefined, UIGameEventEnums.TileNumberAnimationEnd)

        this.animateTiles()
        const totalDiceNumber = this.uiGameManager.gameController.diceState.dice1 + this.uiGameManager.gameController.diceState.dice2
        for(const tileView of this.uiGameManager.mapController.tileViews) {
            if(tileView.diceNumber != totalDiceNumber) continue
            if(tileView.probabilityView == undefined) continue
            const probabilityViewAnimation = new UIGameFlickerAnimation(tileView.probabilityView, this.probabilityViewAnimationWaitMS)
            probabilityViewAnimation.setOnStopCallback(() => this.uiGameManager.eventController.sendEvent(undefined, UIGameEventEnums.TileNumberAnimationEnd))
            probabilityViewAnimation.start()
        }
    }
}
